import htmlParser, { DOMNode, domToReact, Element as ParserElement } from 'html-react-parser'
import { cloneDeep } from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import * as ReactDOMServer from 'react-dom/server'
import styles from './CRE.module.scss'
import { domNodeIsElement, extractAndRemoveStyle, processHtmlLinks } from './cre.utils'
import ChannelContext from '../../context/ChannelContext'
import HistoryContext from '../../context/HistoryContext'
import useLink from '../../hooks/useLink'
import { Type } from '../../interfaces/ChannelInterface'
import {
  CRE_DATA_BLOCK_CONTAINER,
  CRE_DATA_BLOCK_CONTAINER_TRIGGER,
  CRE_DATA_COL_ID,
  CRE_DATA_VIDEO_CONTROLS,
  CRE_DATA_VIDEO_EXT,
  CRE_DATA_VIDEO_SRC,
  CRE_DATA_VIDEO_THUMB,
  CRE_ELEMENT_TYPE,
  CREAppType,
} from '../../services/cre/cre.types'
import { _at, _cms } from '../../utils/translations'
import Link from '../Link/Link'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import Video from '../Video/Video'

type Props = {
  item: CREAppType
  showVideoControls?: boolean
}

export const CRE = (props: Props) => {
  const creApp = props.item

  const channel = useContext(ChannelContext)
  const history = useContext(HistoryContext)
  const linkTools = useLink()
  const [parsedHtml, setParsedHtml] = useState<string | JSX.Element[] | JSX.Element>()
  const blocksToTrigger = useRef<string[]>([])

  const wrapWithLink = (element: JSX.Element | JSX.Element[] | string, href?: string, target?: string, classes?: string) => {
    if (!href) {
      return element
    }
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return (
        <Link to={href} newWindow={target === '_blank'} className={classes}>
          {element}
        </Link>
      )
    }
    return (
      <Link to={href} newWindow={target === '_blank'} className={classes}>
        {element}
      </Link>
    )
  }

  const findNodeColumnParent = (currentNode: ParserElement): ParserElement | null => {
    if (currentNode.attribs && CRE_ELEMENT_TYPE in currentNode.attribs && currentNode.attribs[CRE_ELEMENT_TYPE] === 'column') {
      return currentNode
    }

    const parent = currentNode.parent as DOMNode
    if (parent && domNodeIsElement(parent)) {
      return findNodeColumnParent(parent as ParserElement)
    }

    return null
  }

  const handleElementBlocks = (node: ParserElement) => {
    const triggerId = node.attribs[CRE_DATA_BLOCK_CONTAINER_TRIGGER]

    if (blocksToTrigger.current.includes(node.attribs.class)) {
      return React.cloneElement(domToReact([node]) as React.ReactElement, {
        loading: 'lazy',
        className: `${node.attribs.class} cre-blocks-hidden`,
      })
    }

    if (node.attribs && triggerId) {
      const columnParent = findNodeColumnParent(node)
      if (columnParent) {
        const findTrigger = `${CRE_DATA_BLOCK_CONTAINER_TRIGGER}="${columnParent.attribs[CRE_DATA_COL_ID]}"`
        if (creApp.cre?.content.includes(findTrigger)) {
          blocksToTrigger.current.push(columnParent.attribs[CRE_DATA_COL_ID])
        }
      }

      return React.cloneElement(domToReact([node]) as React.ReactElement, {
        loading: 'lazy',
        onLoad: () => {
          document.querySelector(`[${CRE_DATA_BLOCK_CONTAINER}="${triggerId}"]`)?.classList.add('cre-blocks-loaded')
        },
      })
    }

    if (blocksToTrigger.current.includes(node.attribs[CRE_DATA_BLOCK_CONTAINER])) {
      return React.cloneElement(domToReact([node]) as React.ReactElement, {
        loading: 'lazy',
        className: `${node.attribs.class} cre-blocks-hidden`,
      })
    }
    return node
  }

  const processLinkChildren = (children: ParserElement[]) => {
    const processedChildren = domToReact(children as unknown as DOMNode[])
    const childrenHtml = React.isValidElement(processedChildren)
      ? React.Children.toArray(processedChildren)
          .map(child => (React.isValidElement(child) ? ReactDOMServer.renderToString(child) : String(child)))
          .join('')
      : String(processedChildren)

    return htmlParser(childrenHtml, {
      replace: domNode => {
        if (!domNodeIsElement(domNode)) return domNode
        return handleElementBlocks(domNode as ParserElement)
      },
    })
  }

  const parseContent = async (content: string) => {
    if (!content) return

    const parsedContent = extractAndRemoveStyle(content || '')
    const contentWithFriendlyLinks = await processHtmlLinks(parsedContent.cleanContent, channel.current.canonicalDomain)

    parsedContent.extractedStyles.forEach((style, index) => {
      const existingStyle = document.getElementById(`cre-style-${creApp.id}-${index}`)
      if (existingStyle) {
        existingStyle.remove()
      }

      const styleElement = document.createElement('div')
      styleElement.innerHTML = style
      const styleContent = styleElement.querySelector('style')
      if (styleContent) {
        styleContent.id = `cre-style-${creApp.id}-${index}`
        document.head.appendChild(styleContent)
      }
    })

    // Extract video elements to protect them from href processing
    const videoElements: string[] = []
    const videoPlaceholderPrefix = '___VIDEO_PLACEHOLDER_'

    const contentWithPlaceholders = contentWithFriendlyLinks.replace(
      new RegExp(`<[^>]*${CRE_DATA_VIDEO_SRC}[^>]*>.*?</[^>]+>`, 'gs'),
      match => {
        const placeholder = `${videoPlaceholderPrefix}${videoElements.length}___`
        videoElements.push(match)
        return placeholder
      },
    )

    // Process href attributes (excluding video elements and anchor links)
    const processedHtml = contentWithFriendlyLinks.replace(
      /(<[^>]*?)(?<!data-)href="([^"]+)"([^>]*?>)/g,
      (match, beforeHref, href, afterHref) => {
        if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return match
        }
        return `${beforeHref}href="${linkTools.getHref(href)}"${afterHref}`
      },
    )

    // Restore video elements
    const finalHtml = videoElements.reduce((html, videoElement, index) => {
      return html.replace(`${videoPlaceholderPrefix}${index}___`, videoElement)
    }, processedHtml)

    const _parsedHtml = htmlParser(finalHtml, {
      replace: domNode => {
        if (!domNodeIsElement(domNode)) return domNode

        const node = cloneDeep(domNode) as unknown as ParserElement

        const processedNodes: any[] = []
        for (const child of node.children) {
          const currentChild = child as unknown as ParserElement
          let linkHref = ''
          let linkTarget = ''
          let videoLinkHref = ''
          let videoLinkTarget = ''

          if (node.attribs && node.attribs[CRE_ELEMENT_TYPE] === 'video') {
            const firstChild = node.children[0] as unknown as ParserElement
            if (firstChild && firstChild.attribs) {
              videoLinkTarget = firstChild.attribs.target
              if (firstChild.attribs.href) {
                videoLinkHref = linkTools.getHref(firstChild.attribs.href)
              }
            }
            const showVideoControls =
              node.attribs[CRE_DATA_VIDEO_CONTROLS] === '1' ||
              (!(CRE_DATA_VIDEO_CONTROLS in node.attribs) && (props.showVideoControls || false))

            return (
              <>
                <Video
                  className={node.attribs.class}
                  sources={[{ src: node.attribs[CRE_DATA_VIDEO_SRC], type: node.attribs[CRE_DATA_VIDEO_EXT], withCredentials: true }]}
                  thumbnailSrc={node.attribs[CRE_DATA_VIDEO_THUMB]}
                  options={{ cover: true, fill: false, responsive: true, controls: showVideoControls, fluid: true }}
                  linkHref={videoLinkHref}
                  linkTarget={videoLinkTarget}
                />
              </>
            )
          }

          if (currentChild && currentChild.attribs) {
            linkTarget = currentChild.attribs.target
            if (currentChild.attribs.href) {
              linkHref = currentChild.attribs.href
            }
          }

          if (linkHref) {
            const processedChildren = processLinkChildren(currentChild.children as ParserElement[])
            processedNodes.push(wrapWithLink(processedChildren, linkHref, linkTarget, node.attribs.class) as JSX.Element)
          } else if (currentChild.attribs && currentChild.attribs[CRE_DATA_BLOCK_CONTAINER]) {
            processedNodes.push(handleElementBlocks(node))
          }
        }
        if (processedNodes.length > 0) {
          return processedNodes
        }
      },
    })
    setParsedHtml(_parsedHtml)
  }

  useEffect(() => {
    parseContent(creApp.cre?.content || '')

    // Cleanup function to remove styles when component unmounts
    return () => {
      const stylesToRemove = document.querySelectorAll(`[id^="cre-style-${creApp.id}-"]`)
      stylesToRemove.forEach(style => style.remove())
    }
  }, [creApp.id, creApp.creId, creApp.cre?.id, creApp.cre?.language])

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return

    const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null
    if (!anchor) return

    const href = anchor.getAttribute('href')
    if (!href) return

    if (href.startsWith('http:') || href.startsWith('https:') || href.startsWith('mailto:') || href.startsWith('tel:')) return

    // Hash links — find visible element to handle duplicate IDs (cre-desktop / cre-mobile)
    if (href.startsWith('#')) {
      if (href === '#') return
      const id = href.slice(1)
      const allElements = document.querySelectorAll(`[id="${CSS.escape(id)}"]`)
      if (allElements.length <= 1) return

      e.preventDefault()
      const visibleEl = Array.from(allElements).find(el => {
        let node: HTMLElement | null = el as HTMLElement
        while (node) {
          if (window.getComputedStyle(node).display === 'none') return false
          node = node.parentElement
        }
        return true
      })
      if (visibleEl) {
        visibleEl.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }
    //rest links
    e.preventDefault()
    history.push(href)
  }

  return parsedHtml ? (
    <>
      <div className={styles.container} onClick={handleContainerClick}>
        {parsedHtml}
      </div>
    </>
  ) : (
    <LoadingSpinner margins />
  )
}

export default CRE
